'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import {
  buildAuthUrlForApply,
  pendingAuditionIntent,
  type ApplyJourneyTarget,
} from '~/_lib/apply-journey';

interface AnonymousApplyCtaProps {
  target: ApplyJourneyTarget;
  /** Audition or competition title — persisted to the resume-banner intent. */
  auditionTitle: string;
  /** CTA button label. Caller can pass a category-tagged variant if needed. */
  ctaLabel: string;
  /** Disable the primary button (e.g. competitions waiting for a category pick). */
  disabled?: boolean;
  /**
   * Hide the "Already have an account? Sign in" sub-link beneath the CTA
   * (BAL-342). Competition landing pages render a single clean "Register for
   * Scholarship" CTA; returning users still reach sign-in from the sign-up
   * page itself (`/auth/sign-up` keeps its `next`-preserving sign-in link),
   * so the journey is never stranded — the affordance just moves one step
   * downstream onto the auth screen (consistent with BAL-343). Defaults to
   * `false` so audition surfaces keep the two-line CTA.
   */
  hideSignInLink?: boolean;
}

/**
 * Renders the "Apply" CTA used by anonymous visitors on audition and
 * competition pages, optionally followed by an "already have an account?
 * Sign in" sub-link. Writes a resume-banner intent to localStorage before
 * routing to sign-up.
 */
export function AnonymousApplyCta({
  target,
  auditionTitle,
  ctaLabel,
  disabled,
  hideSignInLink = false,
}: AnonymousApplyCtaProps) {
  const router = useRouter();

  const handleApply = () => {
    pendingAuditionIntent.write({
      surface: target.surface,
      slug: target.slug,
      title: auditionTitle,
      ...(target.styleEntryIds && target.styleEntryIds.length > 0
        ? { styleEntryIds: target.styleEntryIds }
        : {}),
      ...(target.code ? { code: target.code } : {}),
    });
    router.push(buildAuthUrlForApply(target, 'signup'));
  };

  return (
    <div className="space-y-2">
      <Button
        variant="editorial"
        className="h-11 w-full"
        onClick={handleApply}
        disabled={disabled}
      >
        {ctaLabel}
      </Button>
      {!hideSignInLink && (
        <p className="text-muted-foreground text-center text-xs">
          <Trans i18nKey="auth:alreadyHaveAnAccount" />{' '}
          <Link
            href={buildAuthUrlForApply(target, 'signin')}
            className="text-foreground underline underline-offset-2"
          >
            <Trans i18nKey="auth:signIn" />
          </Link>
        </p>
      )}
    </div>
  );
}
